import { useState, type FormEvent, type ReactNode } from 'react';
import { Modal } from '../components/Modal/Modal.js';
import { useClock } from './ClockProvider.js';
import styles from './ClockPicker.module.css';

type Tab = 'countdown' | 'timer' | 'alarms';

export interface ClockPickerProps {
  open: boolean;
  onClose: () => void;
}

const COUNTDOWN_PRESETS = [1, 3, 5, 10, 15, 20] as const;

const COUNTDOWN_NAMES = ['Quiet time', 'Clean up', 'Snack', 'Reading', 'Recess back', 'Group work'];

/**
 * Click-the-clock dialog. Three tabs:
 *   - Countdown: pick minutes + name → starts a countdown that fires
 *                a takeover at zero.
 *   - Timer:     pick a name → starts a stopwatch the teacher stops.
 *   - Alarms:    list of recurring time-of-day alarms.
 *
 * If a countdown or timer is currently running, every tab also shows
 * an "active" card at the top so the teacher can see + stop it.
 */
export function ClockPicker({ open, onClose }: ClockPickerProps) {
  const clock = useClock();
  const [tab, setTab] = useState<Tab>('countdown');

  // If a countdown is running, default the tab to Countdown so the
  // teacher sees the active card; if a timer is running, default to
  // Timer. Otherwise leave whatever was last selected.
  const initialTab: Tab =
    clock.active.kind === 'countdown' ? 'countdown' : clock.active.kind === 'timer' ? 'timer' : tab;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Clock"
      description="Set a countdown, run a timer, or schedule alarms for the day."
      testId="clock-picker"
    >
      <div className={styles.body}>
        <div className={styles.tabs} role="tablist" aria-label="Clock mode">
          <TabButton tab="countdown" current={initialTab} onClick={setTab}>
            Countdown
          </TabButton>
          <TabButton tab="timer" current={initialTab} onClick={setTab}>
            Timer
          </TabButton>
          <TabButton tab="alarms" current={initialTab} onClick={setTab}>
            Alarms
          </TabButton>
        </div>

        {initialTab === 'countdown' ? (
          <CountdownTab onStarted={onClose} />
        ) : initialTab === 'timer' ? (
          <TimerTab onStarted={onClose} />
        ) : (
          <AlarmsTab />
        )}
      </div>
    </Modal>
  );
}

function TabButton({
  tab,
  current,
  onClick,
  children,
}: {
  tab: Tab;
  current: Tab;
  onClick: (t: Tab) => void;
  children: ReactNode;
}) {
  const pressed = tab === current;
  return (
    <button
      type="button"
      role="tab"
      aria-pressed={pressed}
      onClick={() => onClick(tab)}
      className={styles.tab}
      data-testid={`clock-tab-${tab}`}
    >
      {children}
    </button>
  );
}

function ActiveCard() {
  const clock = useClock();
  if (clock.active.kind === 'idle') return null;
  const remainingOrElapsed =
    clock.active.kind === 'countdown'
      ? Math.max(0, clock.active.startedAt + clock.active.durationMs - clock.now)
      : clock.now - clock.active.startedAt;
  return (
    <div className={styles.activeCard}>
      <div className={styles.activeMeta}>
        <span className={styles.activeKind}>
          {clock.active.kind === 'countdown' ? 'Countdown running' : 'Timer running'}
        </span>
        <span className={styles.activeName}>{clock.active.name}</span>
      </div>
      <span className={styles.activeTime}>{formatMs(remainingOrElapsed)}</span>
      <button
        type="button"
        className={styles.stopButton}
        onClick={clock.stopActive}
        data-testid="clock-stop-active"
      >
        Stop
      </button>
    </div>
  );
}

function CountdownTab({ onStarted }: { onStarted: () => void }) {
  const clock = useClock();
  const [minutes, setMinutes] = useState<number>(5);
  const [name, setName] = useState<string>('Quiet time');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (minutes <= 0) return;
    clock.startCountdown(name, minutes * 60 * 1000);
    onStarted();
  };

  return (
    <>
      <ActiveCard />
      <form className={styles.formRow} onSubmit={onSubmit}>
        <div className={styles.formRow}>
          <label className={styles.fieldLabel}>Minutes</label>
          <div className={styles.presets} role="group" aria-label="Countdown presets">
            {COUNTDOWN_PRESETS.map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={minutes === m}
                onClick={() => setMinutes(m)}
                className={styles.preset}
              >
                {m} min
              </button>
            ))}
          </div>
          <input
            type="number"
            className={styles.input}
            min={1}
            max={180}
            value={minutes}
            onChange={(e) => setMinutes(Math.max(1, Math.min(180, Number(e.target.value) || 1)))}
            aria-label="Custom minutes"
            data-testid="countdown-minutes"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="countdown-name">
            What is it for?
          </label>
          <input
            id="countdown-name"
            type="text"
            className={styles.input}
            value={name}
            list="countdown-name-presets"
            placeholder="e.g. Quiet time"
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            data-testid="countdown-name"
          />
          <datalist id="countdown-name-presets">
            {COUNTDOWN_NAMES.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
        </div>

        <button
          type="submit"
          className={styles.startButton}
          disabled={minutes <= 0}
          data-testid="countdown-start"
        >
          Start countdown
        </button>
      </form>
      <p className={styles.hint}>
        When the countdown hits zero, the page chimes and a takeover modal shows the name until you
        tap Stop.
      </p>
    </>
  );
}

function TimerTab({ onStarted }: { onStarted: () => void }) {
  const clock = useClock();
  const [name, setName] = useState<string>('Activity');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clock.startTimer(name);
    onStarted();
  };

  return (
    <>
      <ActiveCard />
      <form className={styles.formRow} onSubmit={onSubmit}>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="timer-name">
            What are you timing?
          </label>
          <input
            id="timer-name"
            type="text"
            className={styles.input}
            value={name}
            placeholder="e.g. Story time"
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            data-testid="timer-name"
          />
        </div>
        <button type="submit" className={styles.startButton} data-testid="timer-start">
          Start timer
        </button>
      </form>
      <p className={styles.hint}>
        The clock pill shows elapsed time until you tap Stop. Useful for pacing — &ldquo;how long
        did clean-up take today?&rdquo;
      </p>
    </>
  );
}

function AlarmsTab() {
  const clock = useClock();
  const [name, setName] = useState<string>('');
  const [hour, setHour] = useState<number>(9);
  const [minute, setMinute] = useState<number>(30);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;
    clock.addAlarm(name, hour, minute);
    setName('');
  };

  const sorted = [...clock.alarms].sort(
    (a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute),
  );

  return (
    <>
      {sorted.length === 0 ? (
        <p className={styles.empty}>No alarms yet — add one below.</p>
      ) : (
        <ul className={styles.alarmList}>
          {sorted.map((al) => (
            <li key={al.id} className={styles.alarmItem}>
              <span className={styles.alarmTime}>{formatTimeOfDay(al.hour, al.minute)}</span>
              <span className={styles.alarmName}>{al.name}</span>
              <button
                type="button"
                className={styles.alarmRemove}
                onClick={() => clock.removeAlarm(al.id)}
                aria-label={`Remove ${al.name}`}
                data-testid={`alarm-remove-${al.id}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <form className={styles.formRow} onSubmit={onSubmit}>
        <div className={styles.formGroup}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="alarm-hour">
              Hour
            </label>
            <input
              id="alarm-hour"
              type="number"
              className={styles.input}
              min={0}
              max={23}
              value={hour}
              onChange={(e) => setHour(Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
              data-testid="alarm-hour"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="alarm-minute">
              Minute
            </label>
            <input
              id="alarm-minute"
              type="number"
              className={styles.input}
              min={0}
              max={59}
              value={minute}
              onChange={(e) => setMinute(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
              data-testid="alarm-minute"
            />
          </div>
          <div className={styles.field} style={{ flex: 2 }}>
            <label className={styles.fieldLabel} htmlFor="alarm-name">
              Name
            </label>
            <input
              id="alarm-name"
              type="text"
              className={styles.input}
              value={name}
              placeholder="e.g. Cleanup"
              maxLength={40}
              onChange={(e) => setName(e.target.value)}
              data-testid="alarm-name"
            />
          </div>
        </div>
        <button
          type="submit"
          className={styles.startButton}
          disabled={!name.trim()}
          data-testid="alarm-add"
        >
          Add alarm
        </button>
      </form>
      <p className={styles.hint}>
        Alarms repeat daily — set &ldquo;9:30 cleanup&rdquo; once and it fires every weekday
        morning. Each alarm fires once per day.
      </p>
    </>
  );
}

// ---------- helpers ----------

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatMs(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}:${pad2(m % 60)}:${pad2(s)}`;
  }
  return `${m}:${pad2(s)}`;
}

function formatTimeOfDay(hour: number, minute: number): string {
  const h12 = hour % 12 || 12;
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  return `${h12}:${pad2(minute)} ${meridiem}`;
}
